import React, { useMemo } from 'react';
import { curveMonotoneX } from '@visx/curve';
import { LineChart } from '../../charts/line-chart';
import { Line } from '../../charts/line';
import { Grid } from '../../charts/grid';
import { XAxis } from '../../charts/x-axis';
import { YAxis } from '../../charts/y-axis';
import { ChartTooltip } from '../../charts/tooltip';
import { ReferenceLine } from '../../charts/reference-line';
import { useChartStable, useYScale } from '../../charts/chart-context';
import { computeReferenceAreaRect } from '../../charts/reference-area-geometry';
import { getFormatter } from '../formatters';
import {
  useIsDarkMode,
  compactFormat,
  seriesColor,
  SYNTH_X_KEY,
  toBklitRows,
  seriesHasData,
  ChartSkeletonLayer,
  isNumeric,
  labelColor,
  RnPointLabels,
  bandColors,
  referenceLabelColor,
} from './chartTheme';
import { isInkShell } from '../../../styles/chartColors';
import { ChartLegend } from './ChartLegend';
import { prefersReducedMotion } from '../AnimatedValue';
import '../ReportsNative.css';

/**
 * Horizontal target band (per the payroll mock) — the kit classifies any
 * child whose displayName is "ReferenceArea" as an underlay (above grid,
 * below series, outside the reveal clip), so this local component fills
 * the ReferenceArea role the vendored kit does not ship a renderer for.
 */
function BandReferenceArea({ y1, y2, fill, fillOpacity = 0.75 }) {
  const { innerWidth, innerHeight, xScale } = useChartStable();
  const yScale = useYScale();
  const rect = computeReferenceAreaRect({
    innerWidth,
    innerHeight,
    y1,
    y2,
    ifOverflow: 'hidden',
    xScale,
    yScale,
  });
  if (!rect) return null;
  return (
    <rect
      x={rect.x}
      y={rect.y}
      width={rect.width}
      height={rect.height}
      fill={fill}
      opacity={fillOpacity}
      pointerEvents="none"
    />
  );
}
BandReferenceArea.displayName = 'ReferenceArea';

/**
 * Two-(or more-)series line chart with white-filled points (per
 * operations-labor.html "$POR vs $PAR" and "Payroll % of Comparable
 * Revenue"). Supports an optional horizontal target band. Rendered with
 * the vendored bklit kit (src/components/charts).
 *
 * Props:
 *   data, xKey, series [{ key, label, color? }]
 *   yFormat     (default 'currency2'), currency
 *   labels      mono point labels on the first series (default false)
 *   band        optional { from, to, label } horizontal target band
 *               (orange-50 fill + dashed top edge, per the payroll mock)
 *   height (default 240), legend (default true), yDomain
 *
 * yDomain: the kit derives domains from the data (no [min, max] override);
 * a positive yDomain[0] is honored by rebasing values so the plot zooms
 * like recharts' domain floor did (ticks/tooltips still show real values).
 */
export function DualLineChart({
  data = [],
  xKey,
  series = [],
  yFormat = 'currency2',
  currency,
  labels = false,
  band,
  height = 240,
  legend = true,
  // 'peak' (default) labels only the top of the scale — the high-water
  // mark. 'full' restores the tick ladder for charts read for values
  // rather than shape.
  yAxis = 'peak',
  yDomain,
  margin,
  loading = false,
}) {
  const dark = useIsDarkMode();
  const fmt = getFormatter(yFormat, currency ? { currency } : undefined);
  const tickFmt = getFormatter(compactFormat(yFormat), currency ? { currency } : undefined);
  const shift = yDomain && isNumeric(yDomain[0]) && yDomain[0] > 0 ? yDomain[0] : 0;

  const rows = useMemo(() => {
    const keys = series.map((s) => s.key);
    const built = toBklitRows(data, keys, { openEnds: true });
    if (shift > 0) {
      for (const r of built) {
        for (const k of keys) {
          if (isNumeric(r[k])) r[k] -= shift;
        }
      }
    }
    return built;
  }, [data, series, shift]);
  const xLabels = useMemo(() => rows.map((r) => String(r.__rnSrc?.[xKey] ?? '')), [rows, xKey]);

  // See TrendAreaChart: the chart stays mounted and only the skeleton layer
  // swaps out when the payload lands.
  const skeleton = loading && rows.length === 0;

  // Per-point markers/labels only make sense on sparse (weekly/monthly)
  // series — on dense daily data they read as scatter noise, and the kit
  // decimates the stroke so dots can sit off the simplified path.
  const sparse = rows.length <= 14;

  const base = margin || { top: labels ? 22 : 10, right: 16, bottom: 0, left: 0 };
  const kitMargin = {
    top: base.top,
    right: base.right,
    bottom: base.bottom + (isInkShell() ? 28 : 34),
    left: base.left + (isInkShell() ? 46 : 52),
  };

  return (
    <div>
      {legend && <ChartLegend series={series} />}
      <div className="rn-chart-body" style={{ height }}>
        <LineChart
          className="rn-bklit"
          data={rows}
          xDataKey={SYNTH_X_KEY}
          xLabels={xLabels}
          margin={kitMargin}
          aspectRatio={null}
          style={{ height: '100%' }}
          animationDuration={prefersReducedMotion() ? 0 : undefined}
        >
          <Grid horizontal numTicksRows={4} />
          <XAxis />
          {!skeleton && <YAxis peak={yAxis === 'peak'} numTicks={4} formatValue={(v) => tickFmt(v + shift)} />}
          {!skeleton && (
            <ChartTooltip
              title={null}
              rows={(point) =>
                series.map((s, i) => ({
                  color: seriesColor(s, i, dark),
                  label: s.label || s.key,
                  value: fmt(point.__rnSrc?.[s.key]),
                }))
              }
            />
          )}
          {band && (
            <BandReferenceArea
              y1={band.from - shift}
              y2={band.to - shift}
              fill={bandColors(dark).fill}
              fillOpacity={0.75}
            />
          )}
          {band && (
            <ReferenceLine
              y={band.to - shift}
              stroke={bandColors(dark).stroke}
              strokeDasharray="4 3"
              label={band.label}
              labelFill={referenceLabelColor(dark)}
              labelFontSize={9}
              labelFontFamily="var(--font-sans)"
            />
          )}
          {skeleton && <ChartSkeletonLayer color={seriesColor(series[0] || {}, 0, dark)} curve={curveMonotoneX} />}
          {series.map((s, i) => {
            if (!seriesHasData(rows, s.key)) return null;
            const color = seriesColor(s, i, dark);
            return (
              <Line
                key={s.key}
                dataKey={s.key}
                curve={curveMonotoneX}
                stroke={color}
                strokeWidth={i === 0 ? 2.5 : 2}
                showMarkers={false}
                strokeDasharray={s.dash}
                dashFromIndex={s.dashFromIndex}
              />
            );
          })}
          {labels && sparse && series.length > 0 && seriesHasData(rows, series[0].key) && (
            <RnPointLabels
              k={series[0].key}
              formatter={(v) => fmt(v + shift)}
              fill={labelColor(dark)}
              fontSize={9.5}
            />
          )}
        </LineChart>
      </div>
    </div>
  );
}

export default DualLineChart;
